import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FiSearch, FiX, FiClock, FiChevronDown, FiRefreshCw, FiDownload } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './LogsInsights.css';
import config from '../config';
import { searchMetadata } from '../api/ticketsApi';
import { fetchApiNames, fetchApiCalls } from '../api/apiMetadataApi';


const quickRangesLeft = [
  'Last 15m', 'Last 30m', 'Last 1h', 'Last 3h',
  'Last 6h', 'Last 12h', 'Last 24h', 'Last 48h',
];

const quickRangesRight = [
  'Last 7 days', 'Today', 'Yesterday', 'This week',
];

const timeUnits = ['seconds', 'minutes', 'hours', 'days', 'weeks', 'months'];

// Searchable, type-to-filter replacement for a plain <select> — used for the
// API Name / API Call filters. Filtering is done client-side over the
// options already fetched via fetchApiMetadata, so typing never triggers a
// network call.
function SearchableDropdown({ options, value, onChange, placeholder, allLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      inputRef.current?.focus();
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div className="li-api-search-dd" ref={wrapRef}>
      <button
        type="button"
        className="li-api-select li-api-search-btn"
        onClick={() => setOpen((p) => !p)}
      >
        <span className="li-api-search-value">{value || placeholder}</span>
        <FiChevronDown className={`li-chevron${open ? ' open' : ''}`} />
      </button>

      {open && (
        <div className="li-api-search-menu">
          <div className="li-api-search-input-wrap">
            <FiSearch className="li-api-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="li-api-search-input"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="li-api-search-list">
            <div
              className={`li-api-search-item${!value ? ' active' : ''}`}
              onClick={() => { onChange(''); setOpen(false); }}
            >
              {allLabel}
            </div>

            {filteredOptions.length === 0 ? (
              <div className="li-api-search-empty">No matches found</div>
            ) : (
              filteredOptions.map((opt) => (
                <div
                  key={opt}
                  className={`li-api-search-item${value === opt ? ' active' : ''}`}
                  onClick={() => { onChange(opt); setOpen(false); }}
                >
                  {opt}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LogsInsights({ accessToken }) {
  const [searchQuery, setSearchQuery]     = useState('');
  const [activeCorrelationId, setActiveCorrelationId] = useState('');
  const [selectedApiName, setSelectedApiName] = useState('');
  const [selectedApiCall, setSelectedApiCall] = useState('');
  const [activeApiName, setActiveApiName] = useState('');
  const [activeApiCall, setActiveApiCall] = useState('');
  const [apiNameOptions, setApiNameOptions] = useState([]);
  const [apiCallOptions, setApiCallOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const [activeTab, setActiveTab]         = useState('quick');
  const [selectedLabel, setSelectedLabel] = useState('Last 15m');
  const [selectedKey, setSelectedKey]     = useState('Last 15m');
  const [quickAmount, setQuickAmount]     = useState(15);
  const [quickUnit, setQuickUnit]         = useState('minutes');
  const [absStart, setAbsStart]           = useState(null);
  const [absEnd, setAbsEnd]               = useState(null);
  const [envDropdownOpen, setEnvDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);
  const buttonRef   = useRef(null);
  const requestIdRef = useRef(0);
  const [tableData, setTableData] = useState([]);
const [loading, setLoading] = useState(false);
// Drives the Refresh (filter-bar) icon's spin animation specifically —
// kept separate from `loading` so the icon doesn't rotate on Search,
// pagination, or any other load that also toggles `loading`.
const [isRefreshing, setIsRefreshing] = useState(false);
const [environment, setEnvironment] = useState('');

const [activeRange, setActiveRange] = useState('Last 15m');

const [rowsPerPage, setRowsPerPage] = useState(25);
const [currentPage, setCurrentPage] = useState(1);
const [totalRecords, setTotalRecords] = useState(0);
const [totalPages, setTotalPages] = useState(1);

// const envOptions = ['qa', 'preprod', 'prod'];

const [envOptions, setEnvOptions] = useState([]);
    const API_BASE_URL = config.apiBaseUrl || process.env.REACT_APP_API_BASE_URL || "https://dashboards.hisglobaltech.com/admin-dashboard";

useEffect(() => {
  // While a correlation-ID search is active, pagination is driven explicitly
  // by fetchByCorrelationId (see handleSearchChange / Prev-Next handlers).
  // Calling the unfiltered fetchData here too races that call: whichever
  // response lands last overwrites totalRecords/totalPages, which is why the
  // page indicator could flip to the unfiltered total (e.g. 1/55) instead of
  // the filtered one (1/3).
  if (activeCorrelationId) return;

  if (environment) {
    fetchData(environment, currentPage, rowsPerPage);
  }
}, [environment, currentPage, rowsPerPage, activeCorrelationId]);

useEffect(() => {
  fetchEnvironments();
}, []);

useEffect(() => {
  fetchApiMetadata();
}, []);

useEffect(() => {
  function handleOutside(e) {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target) &&
      buttonRef.current &&
      !buttonRef.current.contains(e.target)
    ) {
      setDropdownOpen(false);
    };
  }

  document.addEventListener('mousedown', handleOutside);
  return () => document.removeEventListener('mousedown', handleOutside);
}, []);

 const getDateRange = () => {
  const now = new Date();
  let from = new Date(now);

  let amount = 15;
  let unit = 'minutes';

  const source = selectedKey || selectedLabel;

  if (source === 'Yesterday') {
    const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const fromDate = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 0, 0, 0, 0);
    const toDate = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59, 999);
    return {
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
      env: environment
    };
  }

  if (source === 'Today') {
    const fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    return {
      fromDate: fromDate.toISOString(),
      toDate: now.toISOString(),
      env: environment
    };
  }

  const match = source?.match(/(\d+)\s*([a-zA-Z]+)/);

  if (match) {
    amount = parseInt(match[1], 10);

    const u = match[2].toLowerCase();

    switch (u) {
      case 'm':
      case 'min':
      case 'mins':
      case 'minute':
      case 'minutes':
        unit = 'minutes';
        break;

      case 'h':
      case 'hr':
      case 'hrs':
      case 'hour':
      case 'hours':
        unit = 'hours';
        break;

      case 'd':
      case 'day':
      case 'days':
        unit = 'days';
        break;

      case 'w':
      case 'week':
      case 'weeks':
        unit = 'weeks';
        break;

      default:
        unit = 'minutes';
    }
  }

  switch (unit) {
    case 'seconds':
      from.setSeconds(now.getSeconds() - amount);
      break;
    case 'minutes':
      from.setMinutes(now.getMinutes() - amount);
      break;
    case 'hours':
      from.setHours(now.getHours() - amount);
      break;
    case 'days':
      from.setDate(now.getDate() - amount);
      break;
    case 'weeks':
      from.setDate(now.getDate() - amount * 7);
      break;
    case 'months':
      from.setMonth(now.getMonth() - amount);
      break;
  }

  return {
    fromDate: from.toISOString(),
    toDate: now.toISOString(),
    env:environment
  };
};


const fetchEnvironments = () => {
    try {
        const envValue = config.environment || process.env.REACT_APP_ENVIRONMENT;
        const data = Array.isArray(envValue) ? envValue : envValue ? [envValue] : [];

        setEnvOptions(data);

        if (data.length > 0) {
            setEnvironment(data[0]);
        }
    } catch (err) {
        console.error('Env fetch error:', err);
    }
};

const fetchApiMetadata = async () => {
    try {
        const names = await fetchApiNames();
        setApiNameOptions(Array.isArray(names) ? names : []);
    } catch (err) {
        console.error('API Name fetch error:', err);
        setApiNameOptions([]);
    }

    try {
        const calls = await fetchApiCalls();
        setApiCallOptions(Array.isArray(calls) ? calls : []);
    } catch (err) {
        console.error('API Call fetch error:', err);
        setApiCallOptions([]);
    }
};

const fetchData = async (
  envParam = environment,
  page = currentPage,
  size = rowsPerPage,
  mode = activeTab,
  apiNameParam = selectedApiName,
  apiCallParam = selectedApiCall
) => {
    const reqId = ++requestIdRef.current;
    try {
        setLoading(true);
        setActiveApiName(apiNameParam);
        setActiveApiCall(apiCallParam);

let fromDate, toDate;

if (mode === "absolute" && absStart && absEnd) {
  fromDate = absStart.toISOString();
  toDate = absEnd.toISOString();
} else {
  const range = getDateRange();
  fromDate = range.fromDate;
  toDate = range.toDate;
}
       const res = await fetch(`${API_BASE_URL}/api/PerformanceIndicators`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    fromDate,
                    toDate,
                    env: envParam,
                    pageNumber: page,
                    pageSize: size,
                    ...(apiNameParam ? { apiName: apiNameParam } : {}),
                    ...(apiCallParam ? { apiCallName: apiCallParam } : {}),
                }),
            }
        );

        const text = await res.text();
        const data = text ? JSON.parse(text) : {};

        if (reqId !== requestIdRef.current) return; // a newer request superseded this one

        setTableData(data.items || []);
        setTotalRecords(data.totalRecords || 0);
        setTotalPages(data.totalPages || 1);

        setCurrentPage(page);
        setRowsPerPage(size);
        setEnvironment(envParam);

    } catch (err) {
        console.error(err);
    } finally {
        if (reqId === requestIdRef.current) setLoading(false);
    }
};

const fetchByCorrelationId = async (
  id,
  page = currentPage,
  size = rowsPerPage,
  mode = activeTab,
  apiNameParam = selectedApiName,
  apiCallParam = selectedApiCall
) => {
  const reqId = ++requestIdRef.current;
  try {
    setLoading(true);
    setActiveApiName(apiNameParam);
    setActiveApiCall(apiCallParam);

    let fromDate, toDate;

    if (mode === "absolute" && absStart && absEnd) {
      fromDate = absStart.toISOString();
      toDate = absEnd.toISOString();
    } else {
      const range = getDateRange();
      fromDate = range.fromDate;
      toDate = range.toDate;
    }

    const res = await fetch( `${API_BASE_URL}/api/PerformanceIndicators/correlation`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          correlationId: id,
          env: environment,
          fromDate,
          toDate,
          pageNumber: page,
          pageSize: size,
          ...(apiNameParam ? { apiName: apiNameParam } : {}),
          ...(apiCallParam ? { apiCallName: apiCallParam } : {}),
        }),
      }
    );

    const text = await res.text();
    const data = text ? JSON.parse(text) : {};

    if (reqId !== requestIdRef.current) return; // a newer request superseded this one

    setTableData(data.items || []);
    setTotalRecords(data.totalRecords || 0);
    setTotalPages(data.totalPages || 1);
  } catch (err) {
    console.error(err);
    if (reqId === requestIdRef.current) setTableData([]);
  } finally {
    if (reqId === requestIdRef.current) setLoading(false);
  }
};

const handleSearchChange = (e) => {
  setSearchQuery(e.target.value);
};

  const quickSummary = `Last ${quickAmount} ${quickUnit}`;

// TODO: remove once the backend actually filters by apiName/apiCallName —
// today it ignores those fields (confirmed against a live search), so we
// trim the already-fetched rows here as an interim fix.
const filteredTableData = useMemo(() => {
  return tableData.filter(item =>
    (!activeApiName || item.apiName === activeApiName) &&
    (!activeApiCall || item.apiCallName === activeApiCall)
  );
}, [tableData, activeApiName, activeApiCall]);

const chartData = useMemo(() => {
  if (!filteredTableData.length) return { data: [], bucketLabel: '' };
  const timestamps = filteredTableData
    .map(item => item.id?.creationTime ? new Date(item.id.creationTime).getTime() : null)
    .filter(Boolean);
  if (!timestamps.length) return { data: [], bucketLabel: '' };
  const min = Math.min(...timestamps);
  const max = Math.max(...timestamps);
  const range = max - min;
  let bucketMs;
  if (range <= 60 * 60 * 1000)                    bucketMs = 5 * 60 * 1000;       // ≤1h  → 5min
  else if (range <= 6 * 60 * 60 * 1000)           bucketMs = 30 * 60 * 1000;      // ≤6h  → 30min
  else if (range <= 24 * 60 * 60 * 1000)          bucketMs = 60 * 60 * 1000;      // ≤24h → 1h
  else if (range <= 7 * 24 * 60 * 60 * 1000)      bucketMs = 3 * 60 * 60 * 1000;  // ≤7d  → 3h (8 bars/day)
  else                                             bucketMs = 6 * 60 * 60 * 1000;  // >7d  → 6h
  const bucketLabel = bucketMs === 5*60*1000 ? 'per 5 minutes' : bucketMs === 30*60*1000 ? 'per 30 minutes' : bucketMs === 60*60*1000 ? 'per 1 hour' : bucketMs === 3*60*60*1000 ? 'per 3 hours' : 'per 6 hours';
  const buckets = {};
  filteredTableData.forEach(item => {
    if (!item.id?.creationTime) return;
    const t = new Date(item.id.creationTime).getTime();
    const bucket = Math.floor(t / bucketMs) * bucketMs;
    buckets[bucket] = (buckets[bucket] || 0) + 1;
  });
  const data = Object.entries(buckets)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([time, count]) => {
      const d = new Date(Number(time));
      const yyyy = d.getFullYear();
      const mm   = String(d.getMonth() + 1).padStart(2, '0');
      const dd   = String(d.getDate()).padStart(2, '0');
      const hh   = String(d.getHours()).padStart(2, '0');
      const min  = String(d.getMinutes()).padStart(2, '0');
      return { time: `${yyyy}-${mm}-${dd} ${hh}:${min}`, count };
    });
  return { data, bucketLabel };
}, [filteredTableData]);

const FREQ_PARSE = {
  'Last 15m':    { amount: 15, unit: 'minutes' },
  'Last 30m':    { amount: 30, unit: 'minutes' },
  'Last 1h':     { amount: 1,  unit: 'hours'   },
  'Last 3h':     { amount: 3,  unit: 'hours'   },
  'Last 6h':     { amount: 6,  unit: 'hours'   },
  'Last 12h':    { amount: 12, unit: 'hours'   },
  'Last 24h':    { amount: 24, unit: 'hours'   },
  'Last 48h':    { amount: 48, unit: 'hours'   },
  'Last 7 days': { amount: 7,  unit: 'days'    },
  'Today':       { amount: 24, unit: 'hours'   },
  'Yesterday':   { amount: 1,  unit: 'days'    },
  'This week':   { amount: 7,  unit: 'days'    },
};

const handleFreqClick = (label) => {
  setSelectedKey(label);
  setActiveRange(label);
  const parsed = FREQ_PARSE[label];
  if (parsed) {
    setQuickAmount(parsed.amount);
    setQuickUnit(parsed.unit);
  }
};

const isRightPreset = quickRangesRight.includes(selectedKey);

const handleApplyQuick = () => {
  setActiveRange(selectedKey || quickSummary);
  setDropdownOpen(false);
  setSearchQuery('');
  setActiveCorrelationId('');
setCurrentPage(1);
fetchData(environment, 1, rowsPerPage);};

// const handleApplyAbsolute = () => {
//   if (absStart && absEnd) {
//     const fmt = (v) => new Date(v).toLocaleString();
//     setSelectedLabel(`${fmt(absStart)} → ${fmt(absEnd)}`);
//     setSelectedKey('');

//     setDropdownOpen(false); 
// setCurrentPage(1);
// fetchData(environment, 1, rowsPerPage);
//   }
// };

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmtAbsDate = (d) => {
  const dd  = String(d.getDate()).padStart(2, '0');
  const mon = MONTHS[d.getMonth()];
  const yyyy = d.getFullYear();
  const hh  = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mon}-${yyyy} ${hh}:${min}`;
};

const handleApplyAbsolute = () => {
  if (!absStart || !absEnd) return;

  if (absStart > absEnd) {
    alert("Start date cannot be greater than End date");
    return;
  }

  setActiveRange(`${fmtAbsDate(absStart)} → ${fmtAbsDate(absEnd)}`);
  setSelectedKey('');
  setDropdownOpen(false);
  setCurrentPage(1);

  fetchData(environment, 1, rowsPerPage, "absolute");
};

const handleClearSearch = () => {
  setSearchQuery('');
  setActiveCorrelationId('');
setCurrentPage(1);
fetchData(environment, 1, rowsPerPage);
};

const handleApiSearch = () => {
  setCurrentPage(1);
  setActiveCorrelationId(searchQuery);

  if (searchQuery) {
    fetchByCorrelationId(searchQuery, 1, rowsPerPage);
  } else {
    fetchData(environment, 1, rowsPerPage);
  }
};

// Re-runs whichever search is currently active (correlation-ID or plain)
// against the given page/size, reusing the same fetch functions the rest of
// the page already calls.
const refreshCurrentSearch = (page = currentPage, size = rowsPerPage) => {
  if (activeCorrelationId) {
    fetchByCorrelationId(activeCorrelationId, page, size);
  } else {
    fetchData(environment, page, size);
  }
};

// Refresh = clear every search filter (Correlation ID, API Name, API Call)
// and reload the dropdown data, then re-fetch with the now-empty filters.
// selectedApiName/selectedApiCall are passed explicitly as '' rather than
// relying on the just-cleared state, since setState is async and fetchData's
// default params would otherwise still see the stale, pre-clear values.
const handleRefreshFilters = async () => {
  setIsRefreshing(true);
  try {
    setSearchQuery('');
    setActiveCorrelationId('');
    setSelectedApiName('');
    setSelectedApiCall('');
    setActiveApiName('');
    setActiveApiCall('');
    setCurrentPage(1);

    await fetchApiMetadata();

    await fetchData(environment, 1, rowsPerPage, activeTab, '', '');
  } finally {
    setIsRefreshing(false);
  }
};

const handleDownloadMetadata = async (metadataUrl) => {
  try {
    if (!environment) {
      alert('Please select an environment first');
      return;
    }
    if (!metadataUrl) {
      alert('No metadata URL available');
      return;
    }
      const responseData = await searchMetadata(environment, metadataUrl, accessToken);

      // Open response in a new browser tab

      const isXml = responseData.trim().startsWith('<');
      const contentType = isXml ? 'application/xml' : 'application/json';
      const blob = new Blob([responseData], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const newTab = window.open(blobUrl, '_blank');
      if (newTab) newTab.document.title = `Metadata - ${metadataUrl}`;
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error('Error opening metadata:', error);
    alert('Failed to open metadata');
  }
};

const handleDownloadFile = async (metadataUrl) => {
  try {
    if (!environment) { alert('Please select an environment first'); return; }
    if (!metadataUrl) { alert('No metadata URL available'); return; }
    const responseData = await searchMetadata(environment, metadataUrl, accessToken);
    const isXml = responseData.trim().startsWith('<');
    const fileExtension = isXml ? 'xml' : 'json';
    const contentType = isXml ? 'application/xml' : 'application/json';
    const fileName = metadataUrl.split('/').pop().split('?')[0] || `metadata.${fileExtension}`;
    const blob = new Blob([responseData], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error('Error downloading metadata:', error);
    alert('Failed to download metadata');
  }
};

return (
  <section className="logs-insights-section">
    <div className="container">
      <div className='section__header'>
        <h4 className='section--title'>Environment <span className="section--env">{environment.toUpperCase()}</span></h4>
      </div>
      {/* ── Filter bar ── */}
      <div className="li-filter-bar">

        {/* SEARCH */}
        <div className="li-search-wrap">
          <FiSearch className="li-search-icon" />
          <input
            type="text"
            className="li-search-input"
            placeholder="Search by Correlation ID"
            value={searchQuery}
            onChange={handleSearchChange}
          />

          {searchQuery && (
            <button
              className="li-clear-btn"
              onClick={handleClearSearch}
              title="Clear"
            >
              <FiX />
            </button>
          )}
        </div>

        {/* API NAME / API CALL / SEARCH */}
        <div className="li-api-filters">
          <SearchableDropdown
            options={apiNameOptions}
            value={selectedApiName}
            onChange={setSelectedApiName}
            placeholder="Search API Name"
            allLabel="All"
          />

          <SearchableDropdown
            options={apiCallOptions}
            value={selectedApiCall}
            onChange={setSelectedApiCall}
            placeholder="Search API Call Name"
            allLabel="All"
          />

          <button className="li-search-btn" onClick={handleApiSearch}>
            <FiSearch className="li-search-btn-icon" />
            Search
          </button>

          <button
            className="li-refresh-filters-btn"
            onClick={handleRefreshFilters}
            disabled={loading}
            title="Clear filters and reload latest data"
          >
            <FiRefreshCw className={`li-search-btn-icon${isRefreshing ? ' li-spin-icon' : ''}`} />
            Reset
          </button>
        </div>

        {/* ENV DROPDOWN */}
        <div className="li-env-dd">
          {/* <button
            className="li-env-btn"
            onClick={(e) => {
              e.stopPropagation();
              setEnvDropdownOpen((p) => !p);
            }}>
            <span className="li-env-label">Environment</span>
            <span className="li-env-value">{environment.toUpperCase()}</span>
            <span className={`li-env-arrow${envDropdownOpen ? ' open' : ''}`}>
              <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="li-chevron" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
              <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </button> */}

          {envDropdownOpen && (
            <div className="li-env-menu">
              {envOptions.map((env) => (
                <div
                  key={env}
                  className={`li-env-item ${environment === env ? 'active' : ''}`}
                 onClick={(e) => {
                    e.stopPropagation();
                    setEnvironment(env);
                    setCurrentPage(1);
                    setEnvDropdownOpen(false);
                  }}
                >
                  <div className="li-env-main">{env.toUpperCase()}</div>
                  <div className="li-env-desc">
                    {env === 'qa'
                      ? 'Testing Environment'
                      : env === 'preprod'
                        ? 'Staging Environment'
                        : 'Production Environment'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="li-time-wrap">
          <button
            ref={buttonRef}
            className={`li-time-btn ${dropdownOpen ? 'active' : ''}`}
            onClick={() => setDropdownOpen((p) => !p)}
          >
            <FiClock className="li-time-icon" />
            <span className="li-time-label">{activeRange}</span>
            <FiChevronDown
              className={`li-chevron ${dropdownOpen ? 'open' : ''}`}
            />
          </button>

          {dropdownOpen && (
            <div className="li-dropdown" ref={dropdownRef}>
              <div className="li-tabs">
                {['quick', 'absolute'].map((t) => (
                  <button
                    key={t}
                    className={`li-tab ${activeTab === t ? 'active' : ''}`}
                    onClick={() => setActiveTab(t)}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === 'quick' && (
                <div className="li-quick-panel">

                  <div className="li-quick-row">
                    {!isRightPreset && <span className="li-quick-last-label">Last</span>}

                    {!isRightPreset && (
                      <input
                        type="number"
                        className="li-quick-amount"
                        value={quickAmount}
                        min={1}
                        onChange={(e) => { setQuickAmount(e.target.value); setSelectedKey(''); }}
                      />
                    )}

                    {isRightPreset ? (
                      <span className="li-quick-unit li-quick-unit-text">
                        {selectedKey}
                      </span>
                    ) : (
                      <select
                        className="li-quick-unit"
                        value={quickUnit}
                        onChange={(e) => { setQuickUnit(e.target.value); setSelectedKey(''); }}
                      >
                        {timeUnits.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="li-quick-summary">
                    {selectedKey || quickSummary}
                  </div>

                  <div className="li-freq-section">
                    <p className="li-freq-title">
                      Frequently used
                    </p>

                    <div className="li-freq-grid">

                      <div className="li-freq-col">
                        {quickRangesLeft.map((label) => (
                          <button
                            key={label}
                            onClick={() => handleFreqClick(label)}
                            className={`li-freq-btn ${
                              selectedKey === label ? 'selected' : ''
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      <div className="li-freq-col">
                        {quickRangesRight.map((label) => (
                          <button
                            key={label}
                            onClick={() => handleFreqClick(label)}
                            className={`li-freq-btn ${
                              selectedKey === label ? 'selected' : ''
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                    </div>
                  </div>

                  <div className="li-dropdown-footer">
                    <button
                      className="li-btn-cancel"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="li-btn-apply"
                      onClick={handleApplyQuick}
                    >
                      Apply
                    </button>
                  </div>

                </div>
              )}

              {activeTab === 'absolute' && (
                <div className="li-absolute-panel">

                  <div className="li-abs-field">
                    <label className="li-abs-label">Start Date / Time</label>
                    <DatePicker
                      selected={absStart}
                      onChange={(date) => {
                        setAbsStart(date);
                        if (absEnd && date > absEnd) setAbsEnd(null);
                      }}
                      selectsStart
                      startDate={absStart}
                      endDate={absEnd}
                      maxDate={absEnd || new Date()}
                      filterTime={(time) => time <= new Date()}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText="Select start date & time"
                      className="li-abs-input"
                      wrapperClassName="li-abs-datepicker"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      popperPlacement="bottom-start"
                      popperClassName="li-datepicker-popper"
                      popperProps={{ strategy: 'fixed' }}
                      autoComplete="off"
                    />
                  </div>

                  <div className="li-abs-field">
                    <label className="li-abs-label">End Date / Time</label>
                    <DatePicker
                      selected={absEnd}
                      onChange={(date) => {
                        setAbsEnd(date);
                        if (absStart && date < absStart) setAbsStart(null);
                      }}
                      selectsEnd
                      startDate={absStart}
                      endDate={absEnd}
                      minDate={absStart || undefined}
                      maxDate={new Date()}
                      filterTime={(time) => time <= new Date()}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText="Select end date & time"
                      className="li-abs-input"
                      wrapperClassName="li-abs-datepicker"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      popperPlacement="bottom-start"
                      popperClassName="li-datepicker-popper"
                      popperProps={{ strategy: 'fixed' }}
                      autoComplete="off"
                    />
                  </div>

                  <div className="li-dropdown-footer">
                    <button
                      className="li-btn-cancel"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="li-btn-apply"
                      onClick={handleApplyAbsolute}
                    >
                      Apply
                    </button>
                  </div>

                </div>
              )}
            </div>
          )}
        </div>

      </div>

      
       {loading ? ( 
              
          <div className="loader-wrap w-full justify-content-center">
            <div className="spinner"></div>
            <p className="text text-center">Loading Logs & Insights Data...</p>
          </div>
        ) : filteredTableData.length === 0 ? (
          <div className="no-data-wrap">
            <FiSearch size={36} color="#9da2ba" />
            <p className="no-data-title">No Data Found</p>
            <p className="no-data-sub">Selected filter data is not available.</p>
          </div>
        ) : (
        <>

        <div className='li-graph-section'>
          <div className="li-graph-header">
            <span className="li-graph-hits">{totalRecords.toLocaleString()} hits</span>
            <span className="li-graph-range">{activeRange}</span>
          </div>
          {chartData.data.length > 0 && (
            <>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData.data} margin={{ top: 10, right: 16, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#8399aa' }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" height={55} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#8399aa' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #ddd' }} cursor={{ fill: '#1281da11' }} />
                <Bar dataKey="count" fill="#1281DA" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="li-graph-bucket-label">@timestamp {chartData.bucketLabel}</p>
            </>
          )}
        </div>

        {/* ── TABLE ── */}
      <div className="li-table-section">
       
        <div className='li-data-result'>
          Total - <span className='total-count'>{totalRecords} Logs</span>
          <button
            className={`li-refresh-btn${loading ? ' spinning' : ''}`}
            title="Refresh"
            disabled={loading}
            onClick={() => refreshCurrentSearch()}
          >
            <FiRefreshCw size={15} />
          </button>
        </div>
       
        <div className="li-table-wrap">

          <table className="li-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Correlation ID</th>
                {/* <th className='text-center'>Environment</th> */}
                <th>API Name</th>
                <th>API Call</th>
                <th>Service Type</th>
                <th>Request File</th>
                <th>Response File</th>
                <th className='text-center'>Status Code</th>
                <th className='text-center'>Remote Call (s)</th>
                <th className='text-center'>Pre Processing (s)</th>
                <th className='text-center'>Post Processing (s)</th>
                <th className='text-center'>Total Processing (s)</th>
                <th className='text-center'>Requested On</th>
                <th className='text-center'>Responded On</th>
              
              </tr>
            </thead>

            <tbody>
              
                {filteredTableData.map((item, idx) => (
                  <tr key={idx} className="li-row">

                    <td className="li-td-mono">
                      {item.id?.creationTime
                        ? new Date(item.id.creationTime).toLocaleString()
                        : '-'}
                    </td>
                    
                    {/* <td className="li-td-mono">{item.correlationId || '-'}</td> */}

                    <td className="li-td-mono li-td-ids">
                      {item.correlationId
                        ? item.correlationId.split(',').map((id, i, arr) => (
                            <React.Fragment key={i}><span className='d-block'>{id.trim()}{i < arr.length - 1 && <>,</>}</span></React.Fragment>
                          ))
                        : '-'}
                    </td>

                    {/* <td className='text-center'>{item.environment || '-'}</td> */}
                    <td>{item.apiName || '-'}</td>
                    <td>{item.apiCallName || '-'}</td>
                    <td>{item.serviceType || '-'}</td>


                    <td className="li-td-mono text-center">
                      {item.requestContentUrl ? (
                      <span className="li-file-actions">
                        <button type="button" className="li-link-btn"
                          onClick={() => handleDownloadMetadata(item.requestContentUrl)}>
                          Request
                        </button>
                        <button type="button" className="li-download-btn" title="Download"
                          onClick={() => handleDownloadFile(item.requestContentUrl)}>
                          <FiDownload size={13} />
                        </button>
                      </span>
                      ) : (
                      '-'
                      )}
                    </td>
                    <td className="li-td-mono text-center">
                      {item.responseContentUrl ? (
                      <span className="li-file-actions">
                        <button type="button" className="li-link-btn"
                          onClick={() => handleDownloadMetadata(item.responseContentUrl)}>
                          Response
                        </button>
                        <button type="button" className="li-download-btn" title="Download"
                          onClick={() => handleDownloadFile(item.responseContentUrl)}>
                          <FiDownload size={13} />
                        </button>
                      </span>
                      ) : (
                      '-'
                      )}
                    </td>

                    <td className='text-center'>
                      <span className={
                        item.responseStatusCode === 200
                          ? 'badge badge-active'
                          : 'badge badge-inactive'
                      }>
                        {item.responseStatusCode}
                      </span>
                    </td>

                    <td className='text-center'>{item.remoteCallTimeTaken ?? 0}</td>
                    <td className='text-center'>{item.preProcessingTime ?? 0}</td>
                    <td className='text-center'>{item.postProcessingTime ?? 0}</td>
                    <td className='text-center'>{item.totalProcessingTime ?? 0}</td>

                    <td className='li-td-mono text-center'>
                      {item.requestedOn
                        ? new Date(item.requestedOn).toLocaleString()
                        : '-'}
                    </td>

                    <td className='li-td-mono text-center'>
                      {item.respondedOn
                        ? new Date(item.respondedOn).toLocaleString()
                        : '-'}
                    </td> 
                  </tr>
                ))
              }
            </tbody>
          </table>  
        </div>
       
         {/* ── PAGINATION (FIXED PROPER POSITION) ── */}
          <div className="li-pagination-bar">

            {/* ROWS PER PAGE */}
            <div className="li-page-size">
              <span>Rows per page:</span>

              <select
                value={rowsPerPage}
               onChange={(e) => {
                const size = Number(e.target.value);

                setRowsPerPage(size);
                setCurrentPage(1);

                refreshCurrentSearch(1, size);
              }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>
            </div>

            {/* PAGINATION CONTROLS */}
            <div className="li-page-controls">

              <button
                disabled={currentPage === 1}
                onClick={() => {
                  const newPage = currentPage - 1;

                  setCurrentPage(newPage);
                  refreshCurrentSearch(newPage, rowsPerPage);
                }}
              >
                Prev
              </button>

              <span>
                {currentPage} / {totalPages}
              </span>

              <button
                disabled={currentPage === totalPages}
                onClick={() => {
                  const newPage = currentPage + 1;

                  setCurrentPage(newPage);
                  refreshCurrentSearch(newPage, rowsPerPage);
                }}
              >
                Next
              </button>

            </div>

          </div>
       
      </div>

       </>
    )}

    </div>
  </section>
);
}

export default LogsInsights;
